import {
  IGroup,
  ISupportUser,
  IRole,
  IInvite,
  IDnsOption,
  IPackage
} from './management.interface'
import { eManagement } from './management.endpoints'
import { Module } from 'vuex'
import client from '@/config/axios.config'
import _ from 'lodash'
import { PackageForm } from '@/utils/packages'
import { assertSucceeded, failureText } from '@/utils/apiResult'

const management: Module<any, any> = {
  namespaced: true,
  state: {
    groups: [] as IGroup[],
    users: [] as ISupportUser[],
    deletedUsers: [] as ISupportUser[],
    roles: [] as IRole[],
    activeUser: 0 as number,
    usersLoading: false as boolean,
    filter: {
      state: true,
      role: 'all'
    },
    dnsOptions: [] as IDnsOption[],
    dnsLoading: false as boolean,
    packages: [] as IPackage[],
    packagesLoading: false as boolean
  },
  getters: {},
  mutations: {
    setGroups(state, payload) {
      state.groups = payload
    },
    setUsers(state, payload) {
      state.users = payload
    },
    setRoles(state, payload) {
      state.roles = payload
    },
    setDeletedUsers(state, payload) {
      state.deletedUsers = payload
    },
    setFilter(state, payload) {
      state.filter = payload
    },
    setActiveUser(state, payload) {
      state.activeUser = payload
    },
    setUsersLoading(state, payload) {
      state.usersLoading = payload
    }
  },
  actions: {
    getGroups: async ({ commit, state }) => {
      if (!_.isEmpty(state.groups)) return
      const { data } = await client.get(eManagement.groups, {
        params: { page_size: 99 }
      })
      commit('setGroups', data.data)
    },
    getRoles: async ({ commit, state }) => {
      if (!_.isEmpty(state.roles)) return

      const { data } = await client.get(eManagement.roles, {})
      const roles = data.data.filter((e: IRole) => e.name !== 'super_admin')
      commit('setRoles', roles)
    },
    getUsers: async ({ commit, state }) => {
      const params = {
        page_size: 500,
        is_deleted: !state.filter.state,
        is_admin: state.filter.role === 'admin' ? true : null,
        is_support: state.filter.role === 'support' ? true : null
      }
      _.omitBy(params, (p: any) => _.isNull(p))
      try {
        commit('setUsersLoading', true)

        const { data } = await client.get(eManagement.users, {
          params: params
        })
        const users = _.get(data, 'data.page_records') || []
        if (
          params.is_deleted === false &&
          !params.is_admin &&
          !params.is_support
        ) {
          commit('setActiveUser', users.length)
        }
        commit('setUsers', _.get(data, 'data.page_records') || [])
      } finally {
        commit('setUsersLoading', false)
      }
    },
    getDeletedUsers: async ({ commit }) => {
      const { data } = await client.get(eManagement.users, {
        params: { page_size: 500, is_deleted: true }
      })
      commit('setDeletedUsers', _.get(data, 'data.page_records') || [])
    },
    updateUser: async (
      { dispatch },
      payload: { current: ISupportUser; previous?: ISupportUser }
    ) => {
      const { current, previous } = payload

      let changedToAdmin = false

      if (_.isEqual(current, previous) || !previous) return

      // update role
      if (current.user_account_type !== previous.user_account_type) {
        try {
          const { data } = await client.put(
            eManagement.updateUserRole + current.id,
            {
              user_role_type: current.user_account_type
            }
          )
          if (_.get(data, 'data.user_account_type') === 'admin') {
            changedToAdmin = true
          }
        } catch (error) {}
      }
      if (
        current.user_group_name !== previous.user_group_name &&
        !changedToAdmin
      ) {
        try {
          await client.put(eManagement.updateUserGroup + current.id, {
            user_group_name: current.user_group_name
          })
        } catch (error) {}
      }
      dispatch(
        'useToast',
        {
          message: 'User updated successfully',
          type: 'success'
        },
        { root: true }
      )

      dispatch('getUsers')
    },
    deleteUser: async ({ dispatch }, payload: ISupportUser) => {
      try {
        await client.delete(eManagement.deleteUser + payload.id)
      } catch (error) {}
      dispatch('getUsers')
    },
    permaDeleteUser: async ({ dispatch }, payload: ISupportUser) => {
      try {
        await client.delete(eManagement.deleteUser + payload.id, {
          params: { is_permanent: 1 }
        })
      } catch (error) {}
      dispatch('getUsers')
    },
    recoverUser: async ({ dispatch }, payload: ISupportUser) => {
      try {
        await client.post(eManagement.recoverUser + payload.id)
      } catch (error) {}
      dispatch('getUsers')
    },
    setFilter: (context, payload: any) => {
      context.commit('setFilter', payload)

      context.dispatch('getUsers')
    },
    inviteUsers: async (__, payload: IInvite) => {
      try {
        const { data } = await client.post(eManagement.inviteUsers, payload)
        return data
      } catch (error) {
        console.log(error)
      }
    },
    getDnsOptions: async context => {
      try {
        context.state.dnsLoading = true
        const { data } = await client.get(eManagement.getDnsOptions)
        context.state.dnsOptions = (data?.data ?? []).filter(
          (e: any) => e.config_type === 'dns'
        )
        return data
      } catch (error) {
        console.log(error)
      } finally {
        context.state.dnsLoading = false
      }
    },
    deleteDnsOption: async (context, payload: IDnsOption) => {
      try {
        const { data } = await client.delete(
          eManagement.deleteDnsOption + payload.id,
          {
            params: {
              config_type: 'dns'
            },
            skipResponseInterceptor: true
          } as any
        )

        if (data.info.message === 'success') {
          context.dispatch(
            'useToast',
            {
              severity: 'success',
              summary: 'DNS Options deleted successfully',
              detail: ''
            },
            { root: true }
          )
        }
        context.dispatch('getDnsOptions')

        return data
      } catch (error) {
        console.log(error)
      }
    },
    createDnsOption: async (context, payload: IDnsOption) => {
      try {
        const { data } = await client.post(
          eManagement.createDnsOption,
          payload,
          { skipResponseInterceptor: true } as any
        )

        if (data.info.message === 'success') {
          context.dispatch(
            'useToast',
            {
              severity: 'success',
              summary: 'DNS Options created successfully',
              detail: ''
            },
            { root: true }
          )
        }
        context.dispatch('getDnsOptions')

        return data
      } catch (error) {
        if (
          (_.get(error, 'response.data.info.message') ?? '').startsWith(
            'invalid input type'
          )
        ) {
          const details = _.get(error, 'response.data.info.details') ?? {}
          const errors: any[] = []
          Object.entries(details).forEach(([key, value]) => {
            const v = (value ?? '') as string
            // split by space, remove - if any then join with '.'
            const a = key.split(' ')
            _.remove(a, e => e === '-')
            const k = a.join('.')
            const m = v.charAt(0).toUpperCase() + v.slice(1)

            errors.push({
              key: k,
              message: m
            })
          })
          if (errors.length) {
            _.forEach(errors, e => {
              setTimeout(() => {
                context.dispatch(
                  'useToast',
                  {
                    severity: 'error',
                    summary: 'Invalid Input: ' + e.key,
                    detail: e.message,
                    life: 10000
                  },
                  { root: true }
                )
              }, 300)
            })
          }

          throw errors
        } else if (
          (_.get(error, 'response.data.info.message') ?? '').startsWith(
            'Bad request'
          )
        ) {
          const msg = _.get(error, 'response.data.info.details')
          context.dispatch(
            'useToast',
            {
              severity: 'error',
              summary: _.isString(msg) ? msg : 'Bad Request',
              detail: ''
            },
            { root: true }
          )
        }
        throw error
      }
    },
    getPackages: async context => {
      try {
        context.state.packagesLoading = true
        const { data } = await client.get(eManagement.packages)
        // An empty list comes back as {} rather than [].
        context.state.packages = Array.isArray(data?.data) ? data.data : []
      } catch (error) {
        console.log(error)
      } finally {
        context.state.packagesLoading = false
      }
    },
    createPackage: async (context, payload: PackageForm) => {
      try {
        const { data } = await client.post(eManagement.packages, payload, {
          skipResponseInterceptor: true
        } as any)
        assertSucceeded(data)
        context.dispatch(
          'useToast',
          { severity: 'success', summary: 'Package created', detail: '' },
          { root: true }
        )
        context.dispatch('getPackages')
        return data
      } catch (error) {
        context.dispatch(
          'useToast',
          { severity: 'error', summary: failureText(error), detail: '', life: 8000 },
          { root: true }
        )
        throw error
      }
    },
    // `currentKey` names the package being edited; `fields.key` may be a new key, which renames it.
    updatePackage: async (context, payload: { currentKey: string; fields: PackageForm }) => {
      try {
        const { data } = await client.put(
          eManagement.package + encodeURIComponent(payload.currentKey),
          payload.fields,
          { skipResponseInterceptor: true } as any
        )
        assertSucceeded(data)
        context.dispatch(
          'useToast',
          { severity: 'success', summary: 'Package saved', detail: '' },
          { root: true }
        )
        context.dispatch('getPackages')
        return data
      } catch (error) {
        context.dispatch(
          'useToast',
          { severity: 'error', summary: failureText(error), detail: '', life: 8000 },
          { root: true }
        )
        throw error
      }
    },
    deletePackage: async (context, payload: IPackage) => {
      try {
        const { data } = await client.delete(
          eManagement.package + encodeURIComponent(payload.key),
          { skipResponseInterceptor: true } as any
        )
        assertSucceeded(data)
        context.dispatch(
          'useToast',
          { severity: 'success', summary: 'Package deleted', detail: '' },
          { root: true }
        )
        context.dispatch('getPackages')
        return data
      } catch (error) {
        context.dispatch(
          'useToast',
          { severity: 'error', summary: failureText(error), detail: '', life: 8000 },
          { root: true }
        )
        throw error
      }
    }
  }
}

export default management
